import * as React from 'react';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { withRouter, RouteComponentProps } from 'react-router';
import { compose } from 'redux';
import { Drawer } from 'components/';
import * as Queries from 'api/graphql/queries';
import { OrderDirection, OrderFilters, Orders, OrderSortField } from '@api/graphql/types';
import GraphQLClient from '@api/graphql/GraphQLClient';
import ControlledTable from '@components/ControlledTable';
import OrderListFilters from '../components/OrderListFilters';
import { RowInfo } from 'react-table';
import { Column } from 'react-virtualized';
import { Paths } from '..';
import { ApolloQueryResult } from 'apollo-client';
import Notification from 'components/Notification';
import { DispatchProp, connect } from 'react-redux';
import { updateListOptions } from '@actions/listActions';
import { ApplicationState } from 'reducers/';
import { ListOptionType } from '@reducers/listReducer';
import { isNull } from 'util';
import * as Mutations from '@api/graphql/mutations';
import { OpenInNewCell } from '@components/Table/Cell';

interface StateType {
    data: any[];
    paginatorInfo: any;
}

export const OrderListDefaultOptions = {
    sortBy: {
        field: 'created_at',
        direction: OrderDirection.DESC,
    },
    count: 10,
    page: 0,
    filters: {
        id: '',
        create_at_from: '',
        create_at_to: '',
        user_ext_id: '',
        contact_name: '',
        status_id: '',
        shipping_method_id: '',
        is_admin_checked: null,
    },
};

const listName = 'orderList';

const LinkedOrderCell = (row: any) => {
    return row.value ?
        <a href={`${Paths.order}/${row.value}`}>{row.value}</a> :
        '';
};

interface ReduxProps {
    listOptions: ListOptionType;
}

type Props = ReduxProps & DispatchProp & InjectedIntlProps & RouteComponentProps;

class OrderList extends React.Component<Props, StateType> {

    public state = {
        data: [],
        paginatorInfo: {},
    };

    private setAdminChecked = (id: string, value: boolean) => (e: React.SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();
        GraphQLClient.mutate({
            mutation: Mutations.adminModifyOrder,
            variables: { id, is_admin_checked: value },
        }).then(() => {
            this.fetchData();
            Notification.success(this.props.intl.formatMessage({ id: 'notification.succeed' }));
        }).catch((error: any) => {
            Notification.error(this.props.intl.formatMessage({ id: 'notification.failed' }));
        });
    }

    private ActionCell = (row : any) => {
        const isNewCustomer = (isNull(row.original.user) || (row.original.user && row.original.user.orders && row.original.user.orders.paginatorInfo.total === 1)) ?
            <i className="fa fa-star" title={this.label('new_customer.label')} /> :
            '';
        const actionButton = row.original.is_admin_checked ?
            <button className="btn btn-xs pull-right btn-danger" onClick={this.setAdminChecked(row.original.id, false)}>Nem kezelem</button> :
            <button className="btn btn-xs pull-right btn-primary" onClick={this.setAdminChecked(row.original.id, true)}>Kezelem</button>;
        return (
            <>{isNewCustomer} {actionButton}</>
        );
    }

    private OpenCell = OpenInNewCell((rowInfo: any) => {
        return `${Paths.order}/${rowInfo.original.id}`;
    });

    private tableColumns = [
        {Header: '', sortable: false, Cell: this.OpenCell, width: 30},
        this.getDefaultColumnByName('id'),
        this.getDefaultColumnByName('created_at', {sortable: true, width: 140}),
        this.getDefaultColumnByName('user.ext_id'),
        this.getDefaultColumnByName('contact_data.contact_name'),
        this.getDefaultColumnByName('total_sum'),
        this.getDefaultColumnByName('status.code'),
        this.getDefaultColumnByName('shipping_method.name'),
        this.getDefaultColumnByName('linked_order_id', {Cell: LinkedOrderCell}),
        this.getDefaultColumnByName('user', {Header: '', Cell: this.ActionCell, width: 120}),

    ];

    public componentDidMount() {
        this.fetchData();
    }

    public componentDidUpdate(prevProps: Props) {
        if (prevProps.listOptions !== this.props.listOptions) {
            this.fetchData();
        }
    }

    private getDefaultColumnByName(name: string, options: any = {}) {
        return {
            Header: this.label(`${name}.title`),
            accessor: name,
            sortable: false,
            ...options,
        };
    }

    private label(id: string): string {
        return this.props.intl.formatMessage({ id: `module.order.${id}` });
    }

    private getFilters() {
        const result = {...this.props.listOptions.filters};
        Object.keys(result).forEach((key) => !result[key] && delete result[key]);
        if (this.props.listOptions.filters.is_admin_checked === false) {
            result.is_admin_checked = false;
        }
        return result;
    }

    private getSortBy(): {field: string, direction: string} {
        return {
            field: OrderSortField[this.props.listOptions.sortBy.field.toUpperCase()],
            direction: this.props.listOptions.sortBy.direction,
        };
    }

    private fetchData() {
        this.setState({data: []}, () => {
            const variables = {
                ...this.props.listOptions,
                page: this.props.listOptions.page + 1,
                sortBy: this.getSortBy(),
                filters: this.getFilters(),
            };
            GraphQLClient.query<Orders>({
                query: Queries.orders,
                variables,
            }).then((response: ApolloQueryResult<Orders>) => {
                const data = response.data.orders ? response.data.orders.data : [];
                const paginatorInfo = response.data.orders ? response.data.orders.paginatorInfo : {};
                this.setState({data, paginatorInfo});
            }).catch(() => {
                Notification.error(this.props.intl.formatMessage({ id: 'notification.failed' }));
            });
        });
    }

    private onOptionsChange = (options: ListOptionType) => {
        this.props.dispatch(updateListOptions(listName, options));
    }

    private setFilters(values: OrderFilters) {
        const { id, create_at_from, create_at_to, user_ext_id, contact_name, status_id, shipping_method_id, is_admin_checked } = values;
        const options = {
            ...this.props.listOptions,
            filters: {
                id,
                create_at_from,
                create_at_to,
                user_ext_id,
                contact_name,
                status_id,
                shipping_method_id,
                is_admin_checked,
            },
            page: 0,
        };
        this.onOptionsChange(options);
    }

    private onRowClick = (finalState: any, rowInfo?: RowInfo, column?: Column, instance?: any): object | undefined => {
        if (rowInfo) {
            return {
                onClick: () => {
                    this.props.history.push(`${Paths.order}/${rowInfo.original.id}`);
                },
                style: {
                    cursor: 'pointer',
                },
            };
        }
        return {};
    }


    public render() {
        return (
            <Drawer>
                <div className="row">
                    <div className="col-lg-12">
                        <div className="ibox float-e-margins">
                            <div className="ibox-content">
                                <OrderListFilters value={this.props.listOptions.filters} onSubmit={(values: OrderFilters) => { this.setFilters(values); }} />
                                <ControlledTable
                                    listName={listName}
                                    defaultOptions={OrderListDefaultOptions}
                                    columns={this.tableColumns}
                                    options={this.props.listOptions}
                                    data={this.state.data}
                                    paginatorInfo={this.state.paginatorInfo}
                                    onOptionsChange={this.onOptionsChange}
                                    onRowClick={this.onRowClick}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Drawer>
        );
    }
}

const mapStateToProps = ({ lists }: ApplicationState): ReduxProps => {
    return { listOptions: (lists ? (lists[listName] || OrderListDefaultOptions) : OrderListDefaultOptions) };
};

export default compose(
    withRouter,
    injectIntl,
    connect(mapStateToProps),
)(OrderList);



// WEBPACK FOOTER //
// ./src/modules/Order/pages/OrderList.tsx